import React, { useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import SearchSelect from "@ui-components/Select/GenericSelect";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { addExtenderPopCarrierAccessLegAPI } from "../requests/request";
import {
  EMPTY_MANUAL_LEG,
  apiErrorMessage,
  manualLegBody,
  type ManualLegDraft,
  type SiteCarrierAccess,
} from "../extenderPopCarrierAccessData";
import ManualLegFields from "./ManualLegFields";

interface AddManualLegModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
  extenderPopId: number;
  tenantId: number;
  accesses: SiteCarrierAccess[];
  cpes: { id: number; serviceId: string; name: string | null }[];
}

/**
 * Add to a Carrier Access of the site a leg no description names (CTX-830):
 * it is born claimed and the reconciler never touches it.
 */
export default function AddManualLegModal({
  isOpen,
  onClose,
  onAdded,
  extenderPopId,
  tenantId,
  accesses,
  cpes,
}: AddManualLegModalProps) {
  const api = useAxiosAuth0();
  const [carrierAccessId, setCarrierAccessId] = useState<number | null>(
    accesses.length === 1 ? accesses[0].id : null
  );
  const [leg, setLeg] = useState<ManualLegDraft>(EMPTY_MANUAL_LEG);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const body = manualLegBody(leg);

  const save = async () => {
    if (!carrierAccessId || !body) return;
    setIsSaving(true);
    setError(null);
    try {
      await addExtenderPopCarrierAccessLegAPI(
        api,
        extenderPopId,
        carrierAccessId,
        tenantId,
        body
      );
      onAdded();
    } catch (requestError) {
      setError(apiErrorMessage(requestError, "Could not add the leg"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add a leg by hand"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <GenericButton variant="secondary" label="Cancel" onClick={onClose} />
          <GenericButton
            label="Add"
            onClick={save}
            loading={isSaving}
            disabled={isSaving || !carrierAccessId || !body}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <SearchSelect
          label="Carrier Access"
          placeholder="Select a Carrier Access"
          options={accesses.map((access) => ({
            value: String(access.id),
            label: access.name
              ? `${access.serviceId} · ${access.name}`
              : access.serviceId,
          }))}
          defaultValue={carrierAccessId ? String(carrierAccessId) : undefined}
          onSelect={(selected) => setCarrierAccessId(Number(selected))}
        />
        <ManualLegFields
          extenderPopId={extenderPopId}
          tenantId={tenantId}
          cpes={cpes}
          value={leg}
          onChange={setLeg}
        />
        {error && <p className="text-sm text-semantic-text-error">{error}</p>}
      </div>
    </GenericModal>
  );
}
